/**
 * The Tasks page's data layer: how to fetch ONE practice's task list, and how to
 * fold several of them into one as if they were a single practice.
 *
 * Both live here for the same reason their Dashboard counterparts do (see
 * utils/dashboardData.js): the pool is literally the fetch run once per practice
 * and then merged, so the two halves define one contract and cannot be allowed
 * to drift apart.
 *
 * Deliberately deals in RAW API rows, not formatted ones. `formatTask` stays in
 * TasksPage where its ~40 call sites are; keeping it out of here means the merge
 * needs nothing from the page and the page keeps one formatter.
 */
import axios from './axiosInstance';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * One practice's task rows for a time window. `params` is {start_date, end_date}
 * as built by the page's taskWindowParams — always an explicit inclusive range,
 * which the backend resolves as whole Europe/Berlin days.
 */
export const fetchPracticeTasks = async (practiceId, params) => {
  const response = await axios.get(`${API_BASE_URL}/tasks/practice/${practiceId}`, { params });
  return Array.isArray(response.data) ? response.data : [];
};

/**
 * Fold several practices' task rows into ONE list, newest first.
 *
 * The sort is load-bearing, not cosmetic. Each practice arrives already sorted
 * created_at DESC by the backend, so a plain concatenation would read as all of
 * practice A, then all of practice B. The cluster view re-sorts its rows anyway
 * and would have hidden that; the FLAT view returns early without sorting and
 * relies entirely on the order it is handed.
 *
 * Compared as strings, matching how the cluster view already sorts on createdAt:
 * every row is serialized by the same backend from the same timestamptz column,
 * so the ISO-8601 representation is uniform and lexicographic order is
 * chronological order.
 *
 * @param parts array of raw row arrays — one per practice. Entries whose fetch
 *              failed are expected to be filtered out by the caller.
 */
export const mergeTaskLists = (parts) => {
  const rows = [];
  (parts || []).forEach((part) => {
    if (Array.isArray(part)) rows.push(...part);
  });
  rows.sort((a, b) => {
    const x = a?.created_at || '';
    const y = b?.created_at || '';
    return x < y ? 1 : x > y ? -1 : 0;
  });
  return rows;
};
